/* eslint-disable jira/filename-case */
import { v4 as uuid } from 'uuid';
import {
	ACTION_RULE_TYPE,
	CONDITION_RULE_TYPE,
	type ConditionGroup,
	type MetaWorkflowDocumentWithUsages,
	type Rule,
	SCREEN_RULE_TYPE,
	type Transition,
	TRIGGER_RULE_TYPE,
	VALIDATOR_RULE_TYPE,
} from '@jira/project-settings__workflow-types/src/workflowTypes.types.tsx';
import {
	START_NODE_ID,
	transitionType,
} from '@jira/project-settings__workflow-types/src/workflowConstants.tsx';
import type {
	ApiConditionGroup,
	ApiReadViewRule,
	ApiReadViewTransition,
	ApiReadViewWorkflow,
	ApiReadViewWorkflowResponse,
	ApiWorkflowStatus,
} from './ReadViewWorkflowIdentifier.types.tsx';

type StatusId = string;

const flattenConditionsRecursively = (conditionGroup?: ApiConditionGroup): ApiReadViewRule[] => {
	if (!conditionGroup) {
		return [];
	}

	return conditionGroup.conditions.concat(
		conditionGroup.conditionGroups.flatMap(flattenConditionsRecursively),
	);
};

const transformConditionGroupRecursively = (conditionGroup: ApiConditionGroup): ConditionGroup => ({
	id: uuid(),
	operation: conditionGroup.operation,
	conditionGroups: conditionGroup.conditionGroups.map(transformConditionGroupRecursively),
	conditionIds: conditionGroup.conditions.map(({ id }) => id),
});

const transformRule = (rule: ApiReadViewRule): Rule => ({
	id: rule.id,
	displayText: '',
	ruleKey: rule.ruleKey,
	config: rule.parameters || {},
});

const transformRules = (transition: ApiReadViewTransition) => {
	const actions = (transition.actions || []).map((action) => ({
		...action,
		type: ACTION_RULE_TYPE,
	}));
	const conditions = flattenConditionsRecursively(transition.conditions).map((condition) => ({
		...condition,
		type: CONDITION_RULE_TYPE,
	}));
	const validators = (transition.validators || []).map((validator) => ({
		...validator,
		type: VALIDATOR_RULE_TYPE,
	}));
	const triggers = (transition.triggers || []).map((trigger) => ({
		...trigger,
		type: TRIGGER_RULE_TYPE,
	}));
	const screens = transition.transitionScreen
		? [{ ...transition.transitionScreen, type: SCREEN_RULE_TYPE }]
		: [];

	const rules = [...actions, ...conditions, ...validators, ...triggers, ...screens];

	let conditionTree: ConditionGroup | undefined;

	if (transition.conditions) {
		conditionTree = transformConditionGroupRecursively(transition.conditions);
	}

	return {
		rules: rules.map(transformRule),
		conditionTree,
	};
};

export const getTransitionLinks = (transition: ApiReadViewTransition): Transition['links'] => {
	const { links } = transition;

	if (transition.type === transitionType.INITIAL) {
		return [
			{
				fromRef: START_NODE_ID,
				fromPort: 0,
				toPort: links[0]?.toPort ?? 0,
			},
		];
	}

	if (transition.type === transitionType.GLOBAL) {
		return [];
	}

	return links.reduce<Transition['links']>((acc, link) => {
		if (link.fromStatusReference != null) {
			acc.push({
				fromRef: link.fromStatusReference,
				fromPort: link.fromPort ?? undefined,
				toPort: link.toPort ?? undefined,
			});
		}

		return acc;
	}, []);
};

const transformTransitions = (transitions: ApiReadViewTransition[]) => {
	const transformed: Record<string, Transition> = {};

	transitions.forEach((transition) => {
		transformed[transition.id] = {
			id: transition.id,
			type: transitionType[transition.type],
			name: transition.name,
			description: transition.description || '',
			toRef: transition.toStatusReference || undefined,
			links: getTransitionLinks(transition),
			properties: transition.properties || {},
			...transformRules(transition),
		};
	});

	return transformed;
};

const transformSingleWorkflow = (
	response: ApiReadViewWorkflowResponse,
	workflow: ApiReadViewWorkflow,
) => {
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	const allStatuses: Record<string, any> = {};
	const visibleStatuses: Array<{
		ref: StatusId;
	}> = [];

	response.statuses.forEach((status) => {
		const workflowStatus = workflow.statuses.find(
			(candidate: ApiWorkflowStatus) => candidate.statusReference === status.id,
		);

		allStatuses[status.statusReference] = {
			id: status.id,
			name: status.name,
			description: status.description,
			isNew: false,
			statusCategory: status.statusCategory,
			scope: status.scope.type,
			layout:
				workflowStatus?.layout && workflowStatus.layout.x && workflowStatus.layout.y
					? workflowStatus.layout
					: undefined,
			...(workflowStatus?.approvalConfiguration && {
				approvalConfiguration: workflowStatus.approvalConfiguration,
			}),
			properties: workflowStatus?.properties,
		};

		if (workflowStatus?.statusReference) {
			visibleStatuses.push({ ref: workflowStatus.statusReference });
		}
	});

	return {
		id: workflow.id,
		name: workflow.name,
		description: workflow.description,
		versionNumber: workflow.version.versionNumber,
		versionId: workflow.version.id,
		scope: workflow.scope,
		allStatuses,
		visibleStatuses,
		transitions: transformTransitions(workflow.transitions),
		startNodeLayout: workflow.startPointLayout || {},
		loopedTransitionContainerLayout: workflow.loopedTransitionContainerLayout || {},
		isEditable: false,
		associatedIssueTypeIds: [],
		associatedEntityIds: [],
	};
};

export const transform = (
	response: ApiReadViewWorkflowResponse,
): MetaWorkflowDocumentWithUsages => {
	const [workflow] = response.workflows;
	return transformSingleWorkflow(response, workflow);
};

export const transformMultipleWorkflows = (
	response: ApiReadViewWorkflowResponse,
): MetaWorkflowDocumentWithUsages[] => {
	return response.workflows.map((workflow) => {
		return transformSingleWorkflow(response, workflow);
	});
};
